import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, Wine, Anchor, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Events = () => {
  const { t, language } = useLanguage();

  const seo = {
    fr: {
      title: "Événements Henry & Gaby – Hospitalité de prestige & expériences sur-mesure",
      description: "Henry & Gaby conçoit et opère des expériences exclusives autour des grands événements internationaux : hospitalité VIP, aviation privée, rencontres d'affaires et moments d'exception.",
      h1: "Événements & hospitalité Henry & Gaby"
    },
    en: {
      title: "Henry & Gaby Events – Prestigious Hospitality & Bespoke Experiences",
      description: "Henry & Gaby designs and operates exclusive experiences around major international events: VIP hospitality, private aviation, business meetings and exceptional moments.",
      h1: "Henry & Gaby Events & Hospitality"
    }
  };

  const currentSeo = seo[language] || seo.fr;

  const eventTypes = [
    {
      icon: Users,
      title: t.events.type1,
      description: t.events.type1Desc
    },
    {
      icon: Wine,
      title: t.events.type2,
      description: t.events.type2Desc
    },
    {
      icon: Anchor,
      title: t.events.type3,
      description: t.events.type3Desc
    },
    {
      icon: Calendar,
      title: t.events.type4,
      description: t.events.type4Desc
    }
  ];

  const flights = [
    {
      route: 'Paris - Genève',
      code: 'LGG - GVA',
      date: language === 'fr' ? '9 NOV Aller Simple' : '9 NOV One Way',
      price: language === 'fr' ? '3 600 € / Membre' : '€3,600 / Member',
      image: 'https://images.unsplash.com/photo-1616620418653-c2f1603f507d?w=600&q=80',
      jetType: 'Citation CJ2+'
    },
    {
      route: 'Paris - Nice',
      code: 'LGG - NCE',
      date: language === 'fr' ? '18 NOV Aller Simple' : '18 NOV One Way',
      price: language === 'fr' ? '3 500 € / Membre' : '€3,500 / Member',
      image: 'https://images.unsplash.com/photo-1759614581731-4c7090648de0?w=600&q=80',
      jetType: 'Citation XLS+'
    },
    {
      route: 'Frankfurt - Paris',
      code: 'FRA - LBG',
      date: language === 'fr' ? '22 DEC Aller Simple' : '22 DEC One Way',
      price: language === 'fr' ? '3 000 € / Membre' : '€3,000 / Member',
      image: 'https://images.unsplash.com/photo-1570018577507-d6097aab1ffe?w=600&q=80',
      jetType: 'Citation XLS+'
    }
  ];

  return (
    <div>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <link rel="canonical" href="https://henry-and-gaby.com/events" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:url" content="https://henry-and-gaby.com/events" />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src="https://images.unsplash.com/photo-1616207133639-cd5e4db9859f?w=1920&q=80"
            alt="Luxury yacht deck"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(255, 254, 242, 0.9), rgba(255, 254, 242, 0.7))'
          }} />
        </div>

        <div className="container" style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {currentSeo.h1}
          </h1>
          <p className="hero-medium" style={{
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
          }}>
            {t.events.subtitle}
          </p>
        </div>
      </section>

      {/* Event Types */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {eventTypes.map((event, index) => {
              const Icon = event.icon;
              return (
                <div key={index} style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-primary)'
                  }}>
                    <Icon size={24} color="var(--text-primary)" />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                    {event.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {event.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations & Pricing */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              maxWidth: '900px',
              margin: '0 auto 32px',
              lineHeight: 1.8
            }}>
              {language === 'fr' 
                ? 'Découvrez nos vols organisés et sur-mesure dans des conditions VIP exceptionnelles. Voyagez avec d\'autres membres du club pour optimiser les coûts selon notre formule tarifaire exclusive.'
                : 'Discover our organized and tailor-made flights in exceptional VIP conditions. Travel with other club members to optimize costs according to our exclusive pricing formula.'}
            </p>
            <h2 className="hero-medium">
              {language === 'fr' ? 'Exemple de vols H&G pour les membres du club' : 'Example of H&G flights for club members'}
            </h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {flights.map((flight, index) => (
              <div
                key={index}
                className="hover-lift"
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={flight.image}
                  alt={flight.jetType}
                  style={{
                    width: '100%',
                    height: '220px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '24px' }}>
                  <p className="body-small" style={{
                    color: 'var(--text-light)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {flight.jetType}
                  </p>
                  <h3 className="heading-2" style={{ marginBottom: '4px' }}>
                    {flight.route}
                  </h3>
                  <p className="body-regular" style={{
                    color: 'var(--text-secondary)',
                    marginBottom: '12px'
                  }}>
                    {flight.code}
                  </p>
                  <p className="body-regular" style={{
                    color: 'var(--text-secondary)',
                    marginBottom: '12px'
                  }}>
                    {flight.date}
                  </p>
                  <p className="body-large" style={{
                    color: 'var(--text-primary)',
                    fontWeight: 600
                  }}>
                    {flight.price}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Button "Tous nos vols exclusifs" */}
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/member-login" className="btn-primary" style={{ textDecoration: 'none' }}>
              {language === 'fr' ? 'Tous nos vols exclusifs' : 'All our exclusive flights'}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* VIP Events Gallery */}
      <section className="section-padding">
        <div className="container">
          <h2 className="hero-medium" style={{
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Exclusive VIP Events
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px'
          }}>
            <img
              src="https://images.unsplash.com/photo-1750346240784-33b5abaeeabf?w=600&q=80"
              alt="Monaco Grand Prix"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
            <img
              src="https://images.pexels.com/photos/28193003/pexels-photo-28193003.jpeg?w=600&q=80"
              alt="Monaco cityscape"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
            <img
              src="https://images.pexels.com/photos/843633/pexels-photo-843633.jpeg?w=600&q=80"
              alt="Luxury yacht"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
          </div>
          
          {/* Button "Nos events programmés" */}
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/member-login" className="btn-primary" style={{ textDecoration: 'none' }}>
              {language === 'fr' ? 'Nos events programmés' : 'Our scheduled events'}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Exclusive VIP Events' : 'Événements VIP Exclusifs'}
          </h2>
          <p className="hero-medium" style={{
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
            marginBottom: '32px',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            "{t.contact.quote}"
          </p>
          <Link to="/calendar" className="btn-primary" style={{ marginBottom: '48px' }}>
            {language === 'fr' ? 'Voir le calendrier' : 'View calendar'}
            <ArrowRight size={16} />
          </Link>
          
          {/* After-Works Section */}
          <div style={{
            maxWidth: '900px',
            margin: '48px auto 0',
            padding: '48px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '24px' }}>
              {language === 'fr' ? 'Les After-Works de Henry & Gaby' : 'Henry & Gaby After-Works'}
            </h3>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              marginBottom: '32px'
            }}>
              {language === 'fr'
                ? 'Découvrez l\'expérience des élégants After-Works. Des rendez-vous chaque jeudi dans des lieux prestigieux tels que hôtels particuliers, bars Lounges... à Paris, sur la Riviera ou encore aux places les plus dynamiques d\'Europe.'
                : 'Discover the experience of elegant After-Works. Weekly meetings every Thursday in prestigious locations such as private mansions, lounge bars... in Paris, on the Riviera or in the most dynamic places in Europe.'}
            </p>
            <div style={{ textAlign: 'center' }}>
              <Link to="/member-login" className="btn-primary" style={{ textDecoration: 'none' }}>
                {language === 'fr' ? 'Notre prochain after' : 'Our next after-work'}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;